
import { useEffect, useState } from "react";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { Branch } from "@/types/models";

const formSchema = z.object({
  name: z.string().min(1, "El nombre es requerido"),
  address: z.string().min(1, "La dirección es requerida"),
  phone: z.string().min(1, "El teléfono es requerido"),
  operator_percentage: z.number().min(0, "Debe ser >= 0").max(100, "Debe ser <= 100").nullable().optional(),
  administrator_percentage: z.number().min(0, "Debe ser >= 0").max(100, "Debe ser <= 100").nullable().optional(),
}).refine(data => {
  const op = data.operator_percentage;
  const ad = data.administrator_percentage;
  if ((op !== null && op !== undefined) || (ad !== null && ad !== undefined)) {
    if (op === null || op === undefined || ad === null || ad === undefined) {
      return false;
    }
    return (op + ad) === 100;
  }
  return true;
}, {
  message: "Si define porcentajes, ambos deben sumarse para dar 100%",
  path: ["operator_percentage"]
});

type BranchFormValues = z.infer<typeof formSchema>;

interface BranchFormProps {
  branch?: Branch;
  onSuccess: () => void;
}

const BranchForm = ({ branch, onSuccess }: BranchFormProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");

  const form = useForm<BranchFormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: branch?.name || "",
      address: branch?.address || "",
      phone: branch?.phone || "",
      operator_percentage: branch?.operator_percentage ?? null,
      administrator_percentage: branch?.administrator_percentage ?? null,
    },
  });

  useEffect(() => {
    if (branch) {
      form.setValue("name", branch.name);
      form.setValue("address", branch.address);
      form.setValue("phone", branch.phone);
      form.setValue("operator_percentage", branch.operator_percentage ?? null);
      form.setValue("administrator_percentage", branch.administrator_percentage ?? null);
    }
  }, [branch, form]);

  const onSubmit = async (data: BranchFormValues) => {
    setIsSubmitting(true);
    setError("");

    try {
      if (branch) {
        // Actualizar sucursal existente
        const { error: updateError } = await supabase
          .from("branches")
          .update({
            name: data.name,
            address: data.address,
            phone: data.phone,
            operator_percentage: data.operator_percentage,
            administrator_percentage: data.administrator_percentage,
          })
          .eq("id", branch.id);

        if (updateError) throw updateError;
      } else {
        // Crear nueva sucursal
        const { error: insertError } = await supabase
          .from("branches")
          .insert({
            name: data.name,
            address: data.address,
            phone: data.phone,
            operator_percentage: data.operator_percentage,
            administrator_percentage: data.administrator_percentage,
          });
        if (insertError) throw insertError;
      }

      onSuccess();
    } catch (err) {
      console.error("Error al guardar la sucursal:", err);
      setError("Hubo un error al guardar la sucursal. Por favor, intente nuevamente.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Nombre</FormLabel>
              <FormControl>
                <Input placeholder="Nombre de la sucursal" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="address"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Dirección</FormLabel>
              <FormControl>
                <Input placeholder="Dirección" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="phone"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Teléfono</FormLabel>
              <FormControl>
                <Input placeholder="Teléfono" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="grid grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="operator_percentage"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Porcentaje Operador (%)</FormLabel>
                <FormControl>
                  <Input
                    type="number"
                    placeholder="Por defecto"
                    value={field.value ?? ""}
                    onChange={(e) => {
                      const val = e.target.value;
                      field.onChange(val === "" ? null : Number(val));
                    }}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="administrator_percentage"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Porcentaje Admin (%)</FormLabel>
                <FormControl>
                  <Input
                    type="number"
                    placeholder="Por defecto"
                    value={field.value ?? ""}
                    onChange={(e) => {
                      const val = e.target.value;
                      field.onChange(val === "" ? null : Number(val));
                    }}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        {error && <p className="text-sm text-destructive">{error}</p>}

        <div className="flex justify-end gap-2 pt-4">
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting ? "Guardando..." : branch ? "Actualizar" : "Crear"}
          </Button>
        </div>
      </form>
    </Form>
  );
};

export default BranchForm;
